/**
 * Service interfaces — shared contract between real and mock implementations.
 * The app code only depends on these interfaces, never on Supabase/Fly directly.
 */

// ── Auth ──

export type UserProfile = {
  id: string;
  email: string;
  displayName: string;
  assistantName: string;
  customInstructions: string | null;
  avatarUrl: string | null;
  onboardingComplete: boolean;
  modelPreferences: Record<string, string> | null;
  memoryEnabled: boolean;
};

export type AuthResult = {
  user: UserProfile | null;
  error: string | null;
};

export interface AuthService {
  /** Get currently signed-in user, or null */
  getCurrentUser(): Promise<UserProfile | null>;

  /** Listen for auth state changes */
  onAuthChange(callback: (user: UserProfile | null) => void): () => void;

  /** Email + password sign up */
  signUp(email: string, password: string): Promise<AuthResult>;

  /** Email + password sign in */
  signIn(email: string, password: string): Promise<AuthResult>;

  /** OAuth sign in (Google) */
  signInWithOAuth(provider: "google"): Promise<{ error: string | null }>;

  /** Sign out */
  signOut(): Promise<void>;

  /** Update profile fields */
  updateProfile(updates: Partial<Pick<UserProfile, "displayName" | "assistantName" | "customInstructions" | "avatarUrl" | "onboardingComplete" | "modelPreferences" | "memoryEnabled">>): Promise<{ error: string | null }>;

  /** Change password (re-authenticates with current password first) */
  changePassword(currentPassword: string, newPassword: string): Promise<{ error: string | null }>;
}

// ── Credits ──

export type CreditBalance = {
  monthlyCredits: number;
  monthlyUsed: number;
  bonusCredits: number;
  resetDate: string;
};

export type UsageEntry = {
  category: string;
  credits: number;
};

export interface CreditsService {
  /** Fetch current credit balance */
  getBalance(): Promise<CreditBalance>;

  /** Fetch usage breakdown for current month */
  getUsageBreakdown(): Promise<UsageEntry[]>;

  /** Listen for balance changes (pushed from gateway) */
  onBalanceChange(callback: (balance: CreditBalance) => void): () => void;
}

// ── Billing ──

export type SubscriptionPlan = "free" | "pro" | "pro_plus" | "byok";

export type SubscriptionInfo = {
  plan: SubscriptionPlan;
  status: "active" | "canceled" | "past_due";
  stripeCustomerId: string | null;
  currentPeriodEnd: string | null;
  pendingPlan: SubscriptionPlan | null;
  apiKeyHint?: string | null;
  openaiKeyHint?: string | null;
};

export type CreditPackId = "small" | "medium" | "large";

export type CreditPack = {
  id: CreditPackId;
  credits: number;
  priceCents: number;
  label: string;
};

export const CREDIT_PACKS: CreditPack[] = [
  { id: "small", credits: 250, priceCents: 500, label: "$5" },
  { id: "medium", credits: 600, priceCents: 1000, label: "$10" },
  { id: "large", credits: 1500, priceCents: 2000, label: "$20" },
];

export const PLAN_LABELS: Record<SubscriptionPlan, string> = {
  free: "Free",
  pro: "Pro",
  pro_plus: "Pro+",
  byok: "BYOK",
};

export interface BillingService {
  /** Get current subscription info */
  getSubscription(): Promise<SubscriptionInfo>;

  /** Create a Stripe Checkout session and return the redirect URL */
  createCheckoutSession(params: {
    type: "subscription" | "credit-pack";
    plan?: "pro" | "pro_plus" | "byok";
    packSize?: CreditPackId;
    annual?: boolean;
  }): Promise<{ url: string }>;

  /** Create a Stripe Customer Portal session and return the redirect URL */
  createPortalSession(): Promise<{ url: string }>;

  /** Update an existing subscription (upgrade/downgrade between paid plans) */
  updateSubscription(params: { plan: "pro" | "pro_plus" | "byok" }): Promise<{ success: boolean }>;

  /** Cancel a pending downgrade (clears pending_plan) */
  cancelPendingDowngrade(): Promise<{ success: boolean }>;

  /** Save an OpenRouter API key (encrypted server-side) */
  saveApiKey(key: string): Promise<{ hint: string }>;

  /** Delete the stored API key */
  deleteApiKey(): Promise<void>;

  /** Validate an API key against OpenRouter */
  validateApiKey(key: string): Promise<{ valid: boolean; error?: string }>;

  /** Save an OpenAI API key (encrypted server-side, used for TTS) */
  saveOpenaiKey(key: string): Promise<{ hint: string }>;

  /** Delete the stored OpenAI API key */
  deleteOpenaiKey(): Promise<void>;

  /** Validate an API key against OpenAI */
  validateOpenaiKey(key: string): Promise<{ valid: boolean; error?: string }>;
}

// ── Gateway ──

export type GatewayConnectionState = "disconnected" | "connecting" | "connected" | "error";

export type TtsAudioMeta = {
  storagePath: string;
  voice: string;
  text: string;
  durationEstimate: number;
  /** Resolved signed URL (filled by client after fetch) */
  signedUrl?: string;
};

export type ScreenshotMeta = {
  storagePath: string;
  /** Resolved signed URL (filled by client after fetch) */
  signedUrl?: string;
};

export type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  model?: string;
  creditCost?: number;
  channel?: string;
  ttsAudio?: TtsAudioMeta[];
  screenshots?: ScreenshotMeta[];
};

export type ConversationMode = "expert" | "normal" | "lightweight" | "free";

export type SendMessageOptions = {
  sessionId?: string;
  model?: string;
  mode?: ConversationMode;
  /** Base64 data URI of an attached image for vision analysis */
  imageDataUri?: string;
  /** User's preferred vision model for image analysis */
  visionModel?: string;
  /** User's preferred TTS voice */
  ttsVoice?: string;
  /** Tool names the user has approved for credit-costing use (free model gate) */
  approvedToolCosts?: string[];
};

export interface GatewayService {
  /** Current connection state */
  readonly state: GatewayConnectionState;

  /**
   * Whether the gateway persists messages server-side.
   * When true, the app shell should NOT client-side persist messages
   * (the Edge Function handles storage + credit deduction).
   */
  readonly persistsMessages: boolean;

  /** Connect to the gateway */
  connect(): Promise<void>;

  /** Disconnect */
  disconnect(): void;

  /** Send a chat message */
  sendMessage(content: string, options?: SendMessageOptions): Promise<void>;

  /** Listen for connection state changes */
  onStateChange(callback: (state: GatewayConnectionState) => void): () => void;

  /** Listen for incoming messages */
  onMessage(callback: (message: ChatMessage) => void): () => void;

  /** Listen for streaming content updates (optional — real gateway only) */
  onStreamDelta?(
    callback: (messageId: string, content: string) => void
  ): () => void;

  /** Listen for tool activity during streaming (e.g. web search, URL fetch) */
  onToolActivity?(
    callback: (event: ToolActivityEvent) => void
  ): () => void;

  /** Listen for sub-agent spawned events from SSE stream */
  onSubAgentEvent?(
    callback: (event: SubAgentEvent) => void
  ): () => void;

  /** Listen for canvas events from SSE stream */
  onCanvasEvent?(
    callback: (event: CanvasEvent) => void
  ): () => void;
}

export type ToolActivityEvent = {
  type: "tool_start" | "tool_result";
  tools?: string[];
  tool?: string;
};

// ── Channels ──

export type ChannelStatus = "connected" | "disconnected" | "connecting" | "error";

export type ChannelInfo = {
  id: string;
  type: string;
  status: ChannelStatus;
  displayName: string;
  config: Record<string, unknown>;
  errorMessage: string | null;
  connectedAt: string | null;
  createdAt: string;
  botUsername: string | null;
  webhookUrl: string | null;
  lastActivityAt: string | null;
  hasDefaultChatId: boolean;
};

export type ChannelSetupStep = {
  title: string;
  description: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  inputType?: "text" | "password";
  inputKey?: string;
};

export type ChannelSetupConfig = {
  type: string;
  name: string;
  steps: ChannelSetupStep[];
  credentialKeys: string[];
  /** If set, this config is a variant choice — user picks one before wizard. */
  variants?: ChannelSetupVariant[];
};

export type ChannelSetupVariant = {
  type: string;
  label: string;
  description: string;
  badge?: string;
};

export type ValidateCredentialsResult = {
  valid: boolean;
  botUsername?: string;
  error?: string;
};

export type TestChannelResult = {
  ok: boolean;
  error?: string;
};

export interface ChannelsService {
  /** List all connected channels */
  listChannels(): Promise<ChannelInfo[]>;

  /** Get a single channel's current status (for polling during QR pairing etc.) */
  getChannelStatus(id: string): Promise<ChannelInfo | null>;

  /** Add a new channel with optional credentials (returns the created channel) */
  addChannel(type: string, credentials?: Record<string, string>): Promise<ChannelInfo>;

  /** Remove a channel */
  removeChannel(id: string): Promise<void>;

  /** Update channel config (e.g. auto-reply settings) */
  updateChannelConfig(id: string, config: Record<string, unknown>): Promise<void>;

  /** Validate credentials against the channel API without saving */
  validateCredentials(type: string, credentials: Record<string, string>): Promise<ValidateCredentialsResult>;

  /** Get the setup wizard configuration for a channel type */
  getSetupConfig(type: string): ChannelSetupConfig;

  /** Test a connected channel by sending a test message */
  testChannel(id: string): Promise<TestChannelResult>;

  /** Re-register the webhook with the platform (updates allowed_updates, URL, etc.) */
  refreshWebhook(id: string): Promise<{ ok: boolean; error?: string; message?: string }>;
}

// ── Sessions ──

export type SessionInfo = {
  id: string;
  title: string;
  model: string;
  lastMessageAt: string | null;
  messageCount: number;
  channel?: string;
  createdAt: string;
  pinned: boolean;
  tags: string[];
  forkedFromSessionId?: string;
};

export type SessionSearchResult = {
  sessionId: string;
  title: string;
  model: string;
  lastMessageAt: string | null;
  messageCount: number;
  matchContext: string;
  matchSource: "title" | "message";
};

export interface SessionsService {
  /** List all sessions, most recent first */
  listSessions(): Promise<SessionInfo[]>;

  /** Create a new session */
  createSession(title?: string): Promise<SessionInfo>;

  /** Delete a session */
  deleteSession(id: string): Promise<void>;

  /** Rename a session */
  renameSession(id: string, title: string): Promise<void>;

  /** Load all messages for a session, oldest first */
  getMessages(sessionId: string): Promise<ChatMessage[]>;

  /** Persist a single message to a session */
  addMessage(sessionId: string, message: ChatMessage): Promise<void>;

  /** Resolve signed URLs for TTS audio in messages (no-op if no audio) */
  resolveAudioUrls?(messages: ChatMessage[]): Promise<ChatMessage[]>;

  /** Resolve signed URLs for screenshots in messages (no-op if none) */
  resolveScreenshotUrls?(messages: ChatMessage[]): Promise<ChatMessage[]>;

  /** Search sessions by title or message content */
  searchSessions?(query: string): Promise<SessionSearchResult[]>;

  /** Pin or unpin a session */
  pinSession?(id: string, pinned: boolean): Promise<void>;

  /** Update tags on a session */
  setTags?(id: string, tags: string[]): Promise<void>;

  /** Fork a session from a specific message */
  forkSession?(sessionId: string, atMessageId: string, newTitle?: string): Promise<SessionInfo>;
}

// ── Sub-Agents ──

export type SubAgentRunStatus = "pending" | "running" | "completed" | "failed" | "cancelled";

export type SubAgentRun = {
  id: string;
  parentSessionId: string;
  label: string | null;
  task: string;
  status: SubAgentRunStatus;
  resultSummary: string | null;
  resultDetail: string | null;
  error: string | null;
  creditsUsed: number;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
};

export type SubAgentEvent = {
  type: "sub_agent_spawned";
  runId: string;
  label: string;
  task: string;
};

// ── Canvas ──

export type CanvasEventType =
  | "canvas_present"
  | "canvas_hide"
  | "canvas_navigate"
  | "canvas_eval"
  | "canvas_a2ui_push"
  | "canvas_a2ui_reset";

export type CanvasEvent = {
  type: CanvasEventType;
  url?: string;
  jsonl?: string;
  javaScript?: string;
};

export interface SubAgentsService {
  /** List sub-agent runs for a session */
  listRuns(sessionId: string): Promise<SubAgentRun[]>;

  /** Cancel a running sub-agent */
  cancelRun(runId: string): Promise<void>;

  /** Subscribe to real-time status changes for a session's sub-agents */
  onRunUpdate(sessionId: string, callback: (run: SubAgentRun) => void): () => void;
}

// ── Automations (Cron) ──

export type AutomationAction = {
  type: "message";
  content: string;
  model?: string;
  deliverTo?: {
    channelId: string;
    target?: string;
  };
};

export type AutomationInfo = {
  id: string;
  name: string;
  description: string | null;
  schedule: string;
  timezone: string;
  enabled: boolean;
  lastRunAt: string | null;
  nextRunAt: string | null;
  lastError: string | null;
  runCount: number;
  createdAt: string;
  action: AutomationAction;
};

export type CreateAutomationInput = {
  name: string;
  description?: string;
  schedule: string;
  timezone?: string;
  action: AutomationAction;
};

export type AutomationRun = {
  id: string;
  jobId: string;
  startedAt: string;
  completedAt: string | null;
  status: "running" | "success" | "error";
  resultSummary: string | null;
  creditsUsed: number;
  error: string | null;
};

export interface AutomationsService {
  /** List all automations */
  listAutomations(): Promise<AutomationInfo[]>;

  /** Get a single automation by ID */
  getAutomation(id: string): Promise<AutomationInfo | null>;

  /** Create a new automation */
  createAutomation(input: CreateAutomationInput): Promise<AutomationInfo>;

  /** Update an existing automation */
  updateAutomation(id: string, input: Partial<CreateAutomationInput>): Promise<void>;

  /** Toggle automation enabled/disabled */
  toggleAutomation(id: string, enabled: boolean): Promise<void>;

  /** Delete an automation */
  deleteAutomation(id: string): Promise<void>;

  /** Run an automation immediately */
  runAutomation(id: string): Promise<void>;

  /** Get run history for an automation */
  getRunHistory(id: string): Promise<AutomationRun[]>;
}

// ── Devices ──

export type DeviceInfo = {
  id: string;
  name: string;
  type: "macos" | "ios" | "android" | "linux" | "windows";
  lastSeenAt: string | null;
  pairedAt: string;
  capabilities: Record<string, boolean>;
  status: "online" | "offline" | "pairing";
};

export interface DevicesService {
  /** List all paired devices */
  listDevices(): Promise<DeviceInfo[]>;

  /** Remove a paired device */
  removeDevice(id: string): Promise<void>;

  /** Generate a 6-char pairing code for device pairing */
  generatePairingCode(): Promise<{ code: string; expiresAt: string }>;

  /** Poll whether a pairing code has been used */
  getPairingStatus(code: string): Promise<{
    used: boolean;
    deviceId?: string;
    deviceName?: string;
  }>;
}

// ── Integrations ──

export type IntegrationStatus = "connected" | "disconnected" | "connecting" | "error";

export type IntegrationType =
  | "gdrive"
  | "dropbox"
  | "onedrive"
  | "notion"
  | "github"
  | "jira"
  | "linear"
  | "gcal"
  | "ocalendar"
  | "applecal";

export type IntegrationInfo = {
  id: string;
  type: IntegrationType;
  status: IntegrationStatus;
  displayName: string;
  connectedEmail: string | null;
  errorMessage: string | null;
  connectedAt: string | null;
  createdAt: string;
};

export type OAuthConnectResult = {
  success: boolean;
  integration?: IntegrationInfo;
  error?: string;
};

export interface IntegrationsService {
  /** List all integrations (connected + available) */
  listIntegrations(): Promise<IntegrationInfo[]>;

  /** Start the OAuth flow — redirects the page (real) or resolves immediately (mock) */
  startConnect(type: IntegrationType): Promise<void>;

  /** Complete the OAuth flow after redirect — stores provider tokens server-side */
  completeConnect(type: IntegrationType, providerToken: string, providerRefreshToken: string | null): Promise<OAuthConnectResult>;

  /** Disconnect an integration */
  disconnect(id: string): Promise<void>;

  /** Check current status of a connected integration */
  checkStatus(id: string): Promise<IntegrationInfo | null>;
}
